import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { filter, map } from 'rxjs/operators';
import { FormRangeDateModel } from './form-range-date';
import { FormRangeDateUtils } from './form-range-date.utils';
import { RangeSliderValue } from './range-slider';

const FORM_INPUT_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FormRangeDateComponent),
    multi: true
};

/**
 * Eine Komponente, die stellt einen Schieberegler mit Daten dart.
 */
@Component({
    selector: 'common-form-range-date',
    template: `
        <form [formGroup]="group">
            <div class="rangeSliderLabelTop">
                <div class="rangeSliderLabelLeft">{{(model?.from || min) | customDateTime:dateFormat}}</div>
                <div class="rangeSliderLabelRight">{{(model?.to || max) | customDateTime:dateFormat}}</div>
            </div>
            <common-range-slider formControlName="value" (sliderMove)="onSliderMove($event)"></common-range-slider>
            <div class="rangeSliderLabelBottom">
                <div class="rangeSliderLabelLeft">{{min | customDateTime:dateFormat}}</div>
                <div class="rangeSliderLabelRight">{{max | customDateTime:dateFormat}}</div>
            </div>
        </form>
    `,
    providers: [FORM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class FormRangeDateComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

    /**
     * Array mit allen auswählbaren Elementen. Startdatum und Enddatum sind
     * das erste bzw. das letzte Element.
     */
    @Input() range: Date[];

    @Input() dateFormat: string = 'date';

    model: FormRangeDateModel;

    group: UntypedFormGroup;

    min: Date;

    max: Date;

    propagateChangeFn;

    private touch: EventEmitter<FormRangeDateModel> = new EventEmitter<FormRangeDateModel>();

    constructor(private fb: UntypedFormBuilder) {
        this.group = this.fb.group({
            value: []
        });
    }

    ngOnInit() {
        if (!this.range || this.range.length <= 0) {
            throw new Error('FormRangeDateComponent requires "range" input as an array of dates');
        }
        this.registerFormChange();
        this.min = this.range[0];
        this.max = this.range[this.range.length - 1];
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('range')) {
            this.min = changes['range'].currentValue[0];
            this.max = changes['range'].currentValue[this.range.length - 1];
            if (!changes['range'].isFirstChange()) {
                this.writeValue(this.model);
            }
        }
    }

    ngOnDestroy() {
        this.touch.complete();
    }

    writeValue(obj: FormRangeDateModel): void {
        if (!obj) {
            this.group.reset();
            this.model = undefined;
            return;
        }
        this.model = FormRangeDateUtils.prepareModel(obj, this.min, this.max);
        this.group.controls['value']
            .patchValue(FormRangeDateUtils.dateToSliderValue(obj, this.range), {
                onlySelf: true,
                emitEvent: false
            });
    }

    registerOnChange(fn: (FormRangeDateModel) => void): void {
        this.propagateChangeFn = fn;
    }

    registerOnTouched(fn: any): void {
        this.touch.subscribe(fn);
    }

    setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            return this.group.disable({emitEvent: false});
        }
        this.group.enable({emitEvent: false});
    }

    onSliderMove(value: RangeSliderValue) {
        this.model = FormRangeDateUtils.sliderValueToDate(value, this.range);
    }

    private registerFormChange() {
        this.group.get('value').valueChanges
            .pipe(
                filter(x => {
                    return x && x.from !== undefined && x.from !== null && x.to !== undefined && x.to !== null;
                }),
                map(x => FormRangeDateUtils.sliderValueToDate(x, this.range))
            )
            .subscribe(x => this.propagateChangeFn(x));
    }
}
